<template>
  <div class="global-filter-bar">
    <b-row>
      <b-col cols="12" md="3">
        <b-form-group label="Date From:" label-for="date-from">
          <b-form-input
            id="date-from"
            v-model="filters.date_from"
            type="date"
            size="sm"
            @change="emitFilters"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Date To:" label-for="date-to">
          <b-form-input
            id="date-to"
            v-model="filters.date_to"
            type="date"
            size="sm"
            @change="emitFilters"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Branch:" label-for="branch-select">
          <b-form-select
            id="branch-select"
            v-model="filters.branch_id"
            :options="branchOptions"
            :disabled="loadingOptions"
            size="sm"
            @change="emitFilters"
          >
            <template #first>
              <option :value="null">Select Branch</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Project:" label-for="project-select">
          <b-form-select
            id="project-select"
            v-model="filters.project_id"
            :options="projectOptions"
            :disabled="loadingOptions"
            size="sm"
            @change="emitFilters"
          >
            <template #first>
              <option :value="null">Select Project</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Employee/Team:" label-for="employee-select" class="employee-filter-group">
          <multiselect
            id="employee-select"
            ref="employeeMultiselect"
            v-model="selectedEmployees"
            class="employee-multiselect-dropup"
            :options="employeeOptionsList"
            label="displayName"
            track-by="id"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :searchable="true"
            :disabled="loadingOptions"
            placeholder="Select Employee/Team"
            selected-label=""
            select-label=""
            deselect-label=""
            @input="onEmployeesChanged"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Lead Source:" label-for="lead-source-select">
          <b-form-select
            id="lead-source-select"
            v-model="filters.lead_source_id"
            :options="leadSourceOptions"
            :disabled="loadingOptions"
            size="sm"
            @change="emitFilters"
          >
            <template #first>
              <option :value="null">Select Lead Source</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col v-if="showCompanyAssociatePartnerFilter" cols="12" md="3">
        <b-form-group label="Company Associate Partner:" label-for="channel-partner-select">
          <b-form-select
            id="channel-partner-select"
            v-model="filters.channel_partner_id"
            :options="companyAssociatePartnerOptions"
            :disabled="loadingOptions"
            size="sm"
          >
            <template #first>
              <option :value="null">All Company Associate Partners</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3" class="d-flex align-items-end">
        <b-button variant="primary" size="sm" class="mb-1" @click="handleSearch">
          Search
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton } from 'bootstrap-vue';
import Multiselect from 'vue-multiselect';
import moment from 'moment';
import axios from 'axios';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'GlobalFilterBar',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    Multiselect,
  },
  props: {
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
    /** Show Company Associate Partner filter (CP dashboard). Search uses parent + all downline CPs. */
    showCompanyAssociatePartnerFilter: {
      type: Boolean,
      default: false,
    },
    /** When set (CP login), always send this id — parent + downline resolved on server. */
    companyAssociatePartnerScopeId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      filters: {
        date_from: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        date_to: moment().endOf('day').format('YYYY-MM-DD'),
        branch_id: null,
        project_id: null,
        lead_source_id: null,
        channel_partner_id: null,
      },
      selectedEmployees: [],
      employeeOptionsList: [],
      branches: [],
      projects: [],
      leadSources: [],
      companyAssociatePartners: [],
      loadingOptions: false,
    };
  },
  computed: {
    branchOptions() {
      return (this.branches || []).map((b) => ({ value: b.id, text: b.name || '' }));
    },
    projectOptions() {
      return (this.projects || []).map((p) => ({
        value: p.id,
        text: p.projectname || p.name || '',
      }));
    },
    leadSourceOptions() {
      return (this.leadSources || []).map((s) => ({ value: s.id, text: s.name || '' }));
    },
    companyAssociatePartnerOptions() {
      return (this.companyAssociatePartners || []).map((cp) => ({
        value: cp.id,
        text: this.companyAssociatePartnerDisplayName(cp),
      }));
    },
  },
  mounted() {
    this.fetchFilterData();
    this.emitFilters();
  },
  beforeDestroy() {
    this.teardownEmployeeDropdownPortal();
  },
  methods: {
    getHeaders() {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      };
    },
    employeeDisplayName(e) {
      return e.fullname || [e.name, e.surname].filter(Boolean).join(' ') || e.name || '';
    },
    normalizeEmployee(e) {
      const id = Number(e.id);
      return {
        id: Number.isFinite(id) ? id : e.id,
        displayName: this.employeeDisplayName(e),
      };
    },
    companyAssociatePartnerDisplayName(cp) {
      return cp.fullname || [cp.name, cp.surname].filter(Boolean).join(' ') || cp.name || `CP #${cp.id}`;
    },
    buildFilterPayload() {
      const employeeIds = (this.selectedEmployees || [])
        .map((emp) => Number(emp.id))
        .filter((id) => Number.isFinite(id) && id > 0);

      const payload = { ...this.filters };
      if (employeeIds.length > 0) {
        payload.employee_ids = employeeIds;
      }
      const cpId = Number(payload.channel_partner_id);
      const lockedCpId = Number(this.companyAssociatePartnerScopeId);
      if (this.showCompanyAssociatePartnerFilter && Number.isFinite(cpId) && cpId > 0) {
        payload.channel_partner_id = cpId;
      } else if (!this.showCompanyAssociatePartnerFilter && Number.isFinite(lockedCpId) && lockedCpId > 0) {
        payload.channel_partner_id = lockedCpId;
      } else {
        delete payload.channel_partner_id;
      }
      return payload;
    },
    onEmployeesChanged() {
      this.emitFilters();
    },
    onEmployeeDropdownOpen() {
      this.$nextTick(() => {
        const root = this.$refs.employeeMultiselect && this.$refs.employeeMultiselect.$el;
        if (!root) return;

        const wrapper = root.querySelector('.multiselect__content-wrapper');
        if (!wrapper) return;

        this._dropdownWrapper = wrapper;
        this._dropdownAnchor = root;

        if (wrapper.parentNode !== document.body) {
          document.body.appendChild(wrapper);
        }
        wrapper.classList.add('multiselect-dropdown-portal');
        this.positionEmployeeDropdown();
        requestAnimationFrame(() => this.positionEmployeeDropdown());

        this._dropdownScrollHandler = () => this.positionEmployeeDropdown();
        this._dropdownResizeHandler = () => this.positionEmployeeDropdown();
        window.addEventListener('scroll', this._dropdownScrollHandler, true);
        window.addEventListener('resize', this._dropdownResizeHandler);
      });
    },
    onEmployeeDropdownClose() {
      this.teardownEmployeeDropdownPortal();
    },
    positionEmployeeDropdown() {
      const wrapper = this._dropdownWrapper;
      const root = this._dropdownAnchor;
      if (!wrapper || !root) return;

      const rect = root.getBoundingClientRect();
      const gap = 6;
      const maxHeight = 220;
      const wrapperHeight = Math.min(wrapper.offsetHeight || maxHeight, maxHeight);
      const isAbove = root.classList.contains('multiselect--above');

      wrapper.style.position = 'fixed';
      wrapper.style.left = `${rect.left}px`;
      wrapper.style.width = `${rect.width}px`;
      wrapper.style.maxHeight = `${maxHeight}px`;
      wrapper.style.zIndex = '9999';
      wrapper.style.bottom = 'auto';
      wrapper.style.right = 'auto';
      wrapper.style.margin = '0';

      if (isAbove) {
        wrapper.style.top = `${Math.max(8, rect.top - wrapperHeight - gap)}px`;
      } else {
        wrapper.style.top = `${rect.bottom + gap}px`;
      }
    },
    teardownEmployeeDropdownPortal() {
      if (this._dropdownScrollHandler) {
        window.removeEventListener('scroll', this._dropdownScrollHandler, true);
        this._dropdownScrollHandler = null;
      }
      if (this._dropdownResizeHandler) {
        window.removeEventListener('resize', this._dropdownResizeHandler);
        this._dropdownResizeHandler = null;
      }

      const wrapper = this._dropdownWrapper;
      const root = this._dropdownAnchor;
      if (wrapper) {
        wrapper.classList.remove('multiselect-dropdown-portal');
        wrapper.style.position = '';
        wrapper.style.left = '';
        wrapper.style.width = '';
        wrapper.style.top = '';
        wrapper.style.bottom = '';
        wrapper.style.right = '';
        wrapper.style.maxHeight = '';
        wrapper.style.zIndex = '';
        wrapper.style.margin = '';
        if (root && wrapper.parentNode !== root) {
          root.appendChild(wrapper);
        }
      }

      this._dropdownWrapper = null;
      this._dropdownAnchor = null;
    },
    async fetchFilterData() {
      if (!this.baseApi) return;
      this.loadingOptions = true;
      try {
        const tasks = [
          this.fetchBranches(),
          this.fetchProjects(),
          this.fetchEmployees(),
          this.fetchLeadSources(),
        ];
        if (this.showCompanyAssociatePartnerFilter) {
          tasks.push(this.fetchCompanyAssociatePartners());
        }
        await Promise.all(tasks);
      } catch (e) {
        console.error('Error loading dashboard filter options:', e);
      } finally {
        this.loadingOptions = false;
      }
    },
    async fetchBranches() {
      try {
        const res = await axios.get(`${this.baseApi}/getBranch`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        this.branches = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error('Error fetching branches:', e);
        this.branches = [];
      }
    },
    async fetchProjects() {
      try {
        const res = await axios.get(`${this.baseApi}/getallproject`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        this.projects = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error('Error fetching projects:', e);
        this.projects = [];
      }
    },
    async fetchEmployees() {
      try {
        const res = await axios.get(`${this.baseApi}/getEmployee`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        const previousIds = (this.selectedEmployees || []).map((e) => Number(e.id));
        this.employeeOptionsList = (Array.isArray(raw) ? raw : [])
          .map((e) => this.normalizeEmployee(e))
          .filter((e) => e.id !== null && e.id !== undefined && e.id !== '');
        if (previousIds.length) {
          const idSet = new Set(previousIds);
          this.selectedEmployees = this.employeeOptionsList.filter((e) => idSet.has(Number(e.id)));
        }
      } catch (e) {
        console.error('Error fetching employees:', e);
        this.employeeOptionsList = [];
      }
    },
    async fetchLeadSources() {
      try {
        const res = await axios.get(`${this.baseApi}/leadsourcedata`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        this.leadSources = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error('Error fetching lead sources:', e);
        this.leadSources = [];
      }
    },
    async fetchCompanyAssociatePartners() {
      try {
        const res = await axios.get(`${this.baseApi}/getallcp`, { headers: this.getHeaders() });
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const seen = new Set();
        this.companyAssociatePartners = [];
        list.forEach((cp) => {
          const id = Number(cp.id);
          if (!Number.isFinite(id) || id <= 0 || seen.has(id)) {
            return;
          }
          seen.add(id);
          this.companyAssociatePartners.push(cp);
        });
        this.companyAssociatePartners.sort((a, b) =>
          this.companyAssociatePartnerDisplayName(a).localeCompare(this.companyAssociatePartnerDisplayName(b))
        );
      } catch (e) {
        console.error('Error fetching company associate partners:', e);
        this.companyAssociatePartners = [];
      }
    },
    emitFilters() {
      this.$emit('filters-changed', this.buildFilterPayload());
    },
    handleSearch() {
      const payload = this.buildFilterPayload();
      this.$emit('filters-changed', payload);
      this.$emit('search', payload);
    },
  },
};
</script>

<style scoped>
.global-filter-bar {
  padding: 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.global-filter-bar ::v-deep .employee-filter-group {
  position: relative;
  z-index: 2;
}

.global-filter-bar ::v-deep .employee-multiselect-dropup.multiselect {
  min-height: 31px;
  font-size: 0.875rem;
}

.global-filter-bar ::v-deep .employee-multiselect-dropup .multiselect__tags {
  min-height: 31px;
  padding: 4px 36px 2px 10px;
  border: 1px solid #c5c5c5;
  border-radius: 6px;
  background: #fff;
}

.global-filter-bar ::v-deep .employee-multiselect-dropup .multiselect__placeholder {
  color: #9e9e9e;
  padding-top: 2px;
  margin-bottom: 2px;
}

.global-filter-bar ::v-deep .employee-multiselect-dropup .multiselect__tag {
  background: #28a745;
  border-radius: 4px;
  margin-bottom: 2px;
  font-size: 0.75rem;
}

.global-filter-bar ::v-deep .employee-dropdown-header {
  background: #5b9bd5;
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 10px 12px;
}

.global-filter-bar ::v-deep .employee-multiselect-dropup .multiselect__option {
  min-height: 36px;
  padding: 8px 12px;
  color: #333;
  font-size: 0.875rem;
}

.global-filter-bar ::v-deep .employee-multiselect-dropup .multiselect__option--highlight,
.global-filter-bar ::v-deep .employee-multiselect-dropup .multiselect__option--selected {
  background: #28a745 !important;
  color: #fff !important;
}

.global-filter-bar ::v-deep .employee-option-label {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.global-filter-bar ::v-deep .employee-dropdown-empty {
  display: block;
  padding: 12px;
  color: #9e9e9e;
  font-size: 0.8125rem;
  text-align: center;
}
</style>

<!-- Unscoped: portal dropdown is moved to document.body -->
<style>
.multiselect__content-wrapper.multiselect-dropdown-portal {
  z-index: 9999 !important;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
  background: #fff;
  overflow: hidden;
}

.multiselect__content-wrapper.multiselect-dropdown-portal .multiselect__content {
  max-height: 220px;
}

.multiselect__content-wrapper.multiselect-dropdown-portal .employee-dropdown-header {
  background: #5b9bd5;
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 10px 12px;
}

.multiselect__content-wrapper.multiselect-dropdown-portal .multiselect__option {
  min-height: 36px;
  padding: 8px 12px;
  color: #333;
}

.multiselect__content-wrapper.multiselect-dropdown-portal .multiselect__option--highlight,
.multiselect__content-wrapper.multiselect-dropdown-portal .multiselect__option--selected {
  background: #28a745 !important;
  color: #fff !important;
}
</style>
